import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/adjustments")({
  component: AdjustmentsPage,
});

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import {
  useFetchAdjustments,
  useAdjustmentMutations,
} from "@/hooks/api/inventory/useStockOps";
import {
  useFetchWarehouses,
  useFetchItems,
  useFetchUnits,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { AdjustmentTable } from "@/components/inventory/tables/AdjustmentTable";
import { AdjustmentFormSheet } from "@/components/inventory/forms/AdjustmentFormSheet";
import { AdjustmentDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import type {
  StockAdjustmentPayload,
  StockAdjustment,
} from "@/types/inventory";

const REASON_OPTIONS = [
  { label: "Physical Count", value: "PHYSICAL_COUNT" },
  { label: "Correction", value: "CORRECTION" },
  { label: "Opening Balance", value: "OPENING_BALANCE" },
  { label: "Variant Correction", value: "VARIANT_CORRECTION" },
];

function AdjustmentsPage() {
  const navigate = useNavigate();

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [reasonFilter, setReasonFilter] = useState<string | undefined>(
    undefined
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState<StockAdjustment | null>(null);

  const adjustmentsQuery = useFetchAdjustments({
    page_size: pageSize,
    page,
    warehouse: warehouseFilter ? Number(warehouseFilter) : undefined,
    reason: reasonFilter || undefined,
  });
  const adjustments = adjustmentsQuery.data?.results ?? [];
  const count = adjustmentsQuery.data?.count ?? 0;

  const { createAdjustment } = useAdjustmentMutations();
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const itemsQuery = useFetchItems(ALL_ACTIVE_PARAMS);
  const items = itemsQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS);
  const units = unitsQuery.data?.results ?? [];

  const isMutating = createAdjustment.isPending;
  const isLoading = adjustmentsQuery.isPending || isMutating;
  const isFetching = adjustmentsQuery.isFetching || isMutating;

  const resetFilters = () => {
    setWarehouseFilter(undefined);
    setReasonFilter(undefined);
    setPage(1);
  };

  const handleSubmit = async (data: StockAdjustmentPayload) => {
    await createAdjustment.mutateAsync(data);
    toast.success("Stock adjustment recorded");
    setIsSheetOpen(false);
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Stock Adjustments"
          description="Correct discrepancies between system and physical stock counts"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => setIsSheetOpen(true)}
          addLabel="New Adjustment"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SelectFilter
            placeholder="Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={warehouseFilter}
            onChange={(v) => {
              setWarehouseFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Reason"
            options={REASON_OPTIONS}
            value={reasonFilter}
            onChange={(v) => {
              setReasonFilter(v);
              setPage(1);
            }}
          />
        </FiltersPanel>

        <AdjustmentTable
          adjustments={adjustments}
          isLoading={isLoading}
          onView={(a) => setViewTarget(a)}
          onRowClick={(a) =>
            navigate({
              to: "/admin/inventory/adjustments/$id",
              params: { id: String(a.id) },
            })
          }
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={(p) => setPage(p)}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <AdjustmentFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          warehouses={warehouses}
          items={items}
          units={units}
          isLoading={isMutating}
        />

        {viewTarget && (
          <AdjustmentDetailSheet
            isOpen={!!viewTarget}
            onClose={() => setViewTarget(null)}
            record={viewTarget}
          />
        )}
      </div>
    </SidebarInset>
  );
}
